import { getWorldCupTeamBrandingForTeam } from "./worldCupTeamBranding";

export interface TournamentBracketTeamSlot {
  teamId: string | null;
  sourceTeamId: string | null;
  teamName: string | null;
  shortName: string | null;
  abbreviation: string | null;
  seed: number | null;
  score: number | null;
  isWinner: boolean;
  isEliminated: boolean;
  logoUrl: string | null;
  flagEmoji: string | null;
}

export interface TournamentBracketGame {
  id: string;
  sourceGameId: string;
  bracketPositionId: number | null;
  roundKey: string;
  roundOrder: number;
  label: string;
  region: string | null;
  status: string;
  startsAt: string | null;
  updatedAt: string | null;
  isFinal: boolean;
  teams: [TournamentBracketTeamSlot, TournamentBracketTeamSlot];
}

export interface TournamentBracketRound {
  roundKey: string;
  roundOrder: number;
  label: string;
  games: TournamentBracketGame[];
}

export interface TournamentBracketResponse {
  leagueId: string;
  seasonId: string;
  tournamentId: string;
  updatedAt: string | null;
  lastSyncAt: string | null;
  rounds: TournamentBracketRound[];
  firstFourGames: TournamentBracketGame[];
}

export interface WorldCupBracketWeek {
  id: string;
  displayLabel: string;
  roundKey?: string | null;
}

export interface WorldCupBracketTeam {
  id: string;
  name?: string | null;
  city?: string | null;
  abbreviation?: string | null;
}

export interface WorldCupBracketGameSource {
  id: string;
  gameTime?: string | null;
  status?: string | null;
  isCompleted?: boolean | null;
  winnerId?: string | null;
  homeScore?: number | string | null;
  awayScore?: number | string | null;
  homeTeam: WorldCupBracketTeam | null;
  awayTeam: WorldCupBracketTeam | null;
}

export interface BuildWorldCupBracketInput {
  leagueId: string;
  seasonId: string;
  weeks: WorldCupBracketWeek[];
  gamesByWeekId: Map<string, WorldCupBracketGameSource[]>;
}

const parseScore = (value: number | string | null | undefined): number | null => {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === "string" && value.trim().length > 0) {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
};

const normalizeTeamName = (team: WorldCupBracketTeam | null): string | null => {
  if (!team) {
    return null;
  }
  if (typeof team.name === "string" && team.name.trim().length > 0) {
    return team.name.trim();
  }
  if (typeof team.city === "string" && team.city.trim().length > 0) {
    return team.city.trim();
  }
  return null;
};

const buildTeamSlot = (
  team: WorldCupBracketTeam | null,
  score: number | null,
  winnerId: string | null,
  isFinal: boolean,
): TournamentBracketTeamSlot => {
  const teamName = normalizeTeamName(team);
  const abbreviation =
    typeof team?.abbreviation === "string" && team.abbreviation.trim().length > 0
      ? team.abbreviation.trim().toUpperCase()
      : null;
  const branding = getWorldCupTeamBrandingForTeam({
    fifaCode: abbreviation,
    name: teamName,
  });
  const isWinner = Boolean(isFinal && winnerId && team?.id === winnerId);

  return {
    teamId: team?.id ?? null,
    sourceTeamId: null,
    teamName,
    shortName: abbreviation ?? teamName,
    abbreviation,
    seed: null,
    score,
    isWinner,
    isEliminated: Boolean(isFinal && winnerId && team?.id && team.id !== winnerId),
    logoUrl: null,
    flagEmoji: branding?.flagEmoji ?? null,
  };
};

export const buildWorldCupBracket = (
  input: BuildWorldCupBracketInput,
): TournamentBracketResponse => {
  const rounds: TournamentBracketRound[] = input.weeks
    .map((week, roundOrder) => {
      const games = (input.gamesByWeekId.get(week.id) ?? []).map((game, gameIndex) => {
        const isFinal = Boolean(game.isCompleted || game.winnerId);
        const status = game.status?.trim() || (isFinal ? "final" : "scheduled");
        const winnerId = game.winnerId ?? null;

        return {
          id: game.id,
          sourceGameId: game.id,
          bracketPositionId: gameIndex + 1,
          roundKey: week.roundKey ?? week.id,
          roundOrder,
          label: gameIndex === 0 && week.displayLabel ? week.displayLabel : `Match ${gameIndex + 1}`,
          region: null,
          status,
          startsAt: game.gameTime ?? null,
          updatedAt: null,
          isFinal,
          teams: [
            buildTeamSlot(game.homeTeam, parseScore(game.homeScore), winnerId, isFinal),
            buildTeamSlot(game.awayTeam, parseScore(game.awayScore), winnerId, isFinal),
          ],
        } satisfies TournamentBracketGame;
      });

      return {
        roundKey: week.roundKey ?? week.id,
        roundOrder,
        label: week.displayLabel,
        games,
      } satisfies TournamentBracketRound;
    })
    .filter((round) => round.games.length > 0);

  return {
    leagueId: input.leagueId,
    seasonId: input.seasonId,
    tournamentId: `world-cup:${input.seasonId}`,
    updatedAt: null,
    lastSyncAt: null,
    rounds,
    firstFourGames: [],
  };
};
